import {
  Action,
  combineReducers,
  configureStore,
  ConfigureStoreOptions,
  ThunkAction,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { hydrateReducer } from '@doda/common/src/redux/utils/hydrateReducer';
import {
  savedSearchesInitialState,
  savedSearchesReducer,
} from '@doda/common/src/redux/features/savedSearches';
import {
  commonInitialState,
  commonReducer,
} from '@doda/common/src/redux/store';
import { mergeReducer } from '@doda/common/src/redux/utils/mergeReducer';
import { isDevelopment } from '@doda/common/src/utils/isDevelopment';
import { rtkQueryMiddleWares } from '@doda/common/src/redux/rtkQuery';

import {
  jobSearchDetailInitialState,
  jobSearchDetailReducer,
} from '../pages/DodaFront/View/JobSearchDetail/redux/features/jobSearchDetail';
import {
  bookmarkInitialState,
  bookmarkReducer,
} from '../pages/DodaFront/View/JobSearchDetail/redux/slices/bookmark';
import {
  jobDetailHighlyRecommendABTestInitialState,
  jobDetailHighlyRecommendABTestReducer,
} from '../pages/DodaFront/View/JobSearchDetail/redux/slices/jobDetailHighlyRecommendABTest';
import {
  careerRecommendJobListInitialState,
  careerRecommendJobListReducer,
} from './features/careerRecommendJobList';
import {
  initialState as jobListInitialState,
  jobListReducer,
} from './features/jobList';
import {
  rewritesUrlInitialState,
  rewritesUrlReducer,
} from './features/rewritesUrl';
import {
  uiInitialState,
  uiReducer,
} from '../pages/dcfront/referredJob/referredJobDetail/redux/uiSlice';
import {
  recommendModalInitialState,
  recommendModalReducer,
} from './features/recommendModal';
import {
  bookmarkAppealModalInitialState,
  bookmarkAppealModalReducer,
} from './features/bookmarkAppealModalSlice';

const reducers = combineReducers({
  rewritesUrl: rewritesUrlReducer,
  jobList: jobListReducer,
  savedSearches: savedSearchesReducer,
  careerRecommendJobList: careerRecommendJobListReducer,
  jobSearchDetail: jobSearchDetailReducer,
  jobDetailHighlyRecommendABTest: jobDetailHighlyRecommendABTestReducer,
  ui: uiReducer,
  recommendModal: recommendModalReducer,
  bookmarkAppealModal: bookmarkAppealModalReducer,
  // NOTE: 以下の slice たちは common 配下で dispatch・selector 実行しているので必ず common と同じ形に合わせる
  abTest: commonReducer.abTest,
  analytics: commonReducer.analytics,
  canonical: commonReducer.canonical,
  intentionJobList: commonReducer.intentionJobList,
  jobTrendRanking: commonReducer.jobTrendRanking,
  jobRecommendOffset: commonReducer.jobRecommendOffset,
  jobRecommendListForTrendIncome: commonReducer.jobRecommendListForTrendIncome,
  jobRecommendListForTrendEstablish:
    commonReducer.jobRecommendListForTrendEstablish,
  jobRecommendListForTrendEmploymentStatus:
    commonReducer.jobRecommendListForTrendEmploymentStatus,
  jobRecommendListForTrendEmployeeAverageAge:
    commonReducer.jobRecommendListForTrendEmployeeAverageAge,
  jobRecommendListForTrendLanguageAndPositions:
    commonReducer.jobRecommendListForTrendLanguageAndPositions,
  jobRecommendListForTrendWelfareBenefits:
    commonReducer.jobRecommendListForTrendWelfareBenefits,
  jobRecommendListForTrendHolidayAndWayOfWorking:
    commonReducer.jobRecommendListForTrendHolidayAndWayOfWorking,
  jobRecommendListForTrendWorkplaceEnvironment:
    commonReducer.jobRecommendListForTrendWorkplaceEnvironment,
  jobRecommendListForTrendRequirements:
    commonReducer.jobRecommendListForTrendRequirements,
  jobTrendRecommendList: commonReducer.jobTrendRecommendList,
  jobCount: commonReducer.jobCount,
  master: commonReducer.master,
  memberInfo: commonReducer.memberInfo,
  plpLinkList: commonReducer.plpLinkList,
  precounseling: commonReducer.precounseling,
  prefOnlyFlg: commonReducer.prefOnlyFlg,
  queryParams: commonReducer.queryParams,
  // 紹介求人詳細は common 配下の slice を、他は pc 配下の slice を使っているため
  // TODO: 使ってる slice を統一させる
  bookmark: mergeReducer(bookmarkReducer, commonReducer.bookmark),
  searchLabel: commonReducer.searchLabel,
  seoText: commonReducer.seoText,
  jobOccupationRanking: commonReducer.jobOccupationRanking,
  featureFlag: commonReducer.featureFlag,
  imageOrigin: commonReducer.imageOrigin,
  companyBlockAppeal: commonReducer.companyBlockAppeal,
  matchedTags: commonReducer.matchedTags,
});

const initialState = {
  rewritesUrl: rewritesUrlInitialState,
  jobList: jobListInitialState,
  savedSearches: savedSearchesInitialState,
  careerRecommendJobList: careerRecommendJobListInitialState,
  jobSearchDetail: jobSearchDetailInitialState,
  jobDetailHighlyRecommendABTest: jobDetailHighlyRecommendABTestInitialState,
  ui: uiInitialState,
  recommendModal: recommendModalInitialState,
  bookmarkAppealModal: bookmarkAppealModalInitialState,
  // NOTE: 以下の slice たちは common 配下で dispatch・selector 実行しているので必ず common と同じ形に合わせる
  abTest: commonInitialState.abTest,
  analytics: commonInitialState.analytics,
  canonical: commonInitialState.canonical,
  intentionJobList: commonInitialState.intentionJobList,
  jobTrendRanking: commonInitialState.jobTrendRanking,
  jobRecommendOffset: commonInitialState.jobRecommendOffset,
  jobRecommendListForTrendIncome:
    commonInitialState.jobRecommendListForTrendIncome,
  jobRecommendListForTrendEstablish:
    commonInitialState.jobRecommendListForTrendEstablish,
  jobRecommendListForTrendEmploymentStatus:
    commonInitialState.jobRecommendListForTrendEmploymentStatus,
  jobRecommendListForTrendEmployeeAverageAge:
    commonInitialState.jobRecommendListForTrendEmployeeAverageAge,
  jobRecommendListForTrendLanguageAndPositions:
    commonInitialState.jobRecommendListForTrendLanguageAndPositions,
  jobRecommendListForTrendWelfareBenefits:
    commonInitialState.jobRecommendListForTrendWelfareBenefits,
  jobRecommendListForTrendHolidayAndWayOfWorking:
    commonInitialState.jobRecommendListForTrendHolidayAndWayOfWorking,
  jobRecommendListForTrendWorkplaceEnvironment:
    commonInitialState.jobRecommendListForTrendWorkplaceEnvironment,
  jobRecommendListForTrendRequirements:
    commonInitialState.jobRecommendListForTrendRequirements,
  jobTrendRecommendList: commonInitialState.jobTrendRecommendList,
  jobCount: commonInitialState.jobCount,
  master: commonInitialState.master,
  memberInfo: commonInitialState.memberInfo,
  plpLinkList: commonInitialState.plpLinkList,
  precounseling: commonInitialState.precounseling,
  prefOnlyFlg: commonInitialState.prefOnlyFlg,
  queryParams: commonInitialState.queryParams,
  bookmark: { ...bookmarkInitialState, ...commonInitialState.bookmark },
  searchLabel: commonInitialState.searchLabel,
  seoText: commonInitialState.seoText,
  jobOccupationRanking: commonInitialState.jobOccupationRanking,
  featureFlag: commonInitialState.featureFlag,
  imageOrigin: commonInitialState.imageOrigin,
  companyBlockAppeal: commonInitialState.companyBlockAppeal,
  matchedTags: commonInitialState.matchedTags,
};

export const reducer = hydrateReducer(reducers, initialState);

export type RootState = ReturnType<typeof reducer>;

export type AppThunk<ReturnType = unknown> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action
>;

export type AppDispatch = ThunkDispatch<RootState, unknown, Action>;
export type AppStore = ReturnType<typeof makeStore>;

type MakeStoreArgs = Pick<ConfigureStoreOptions, 'preloadedState'>;

/**
 * PC の store を生成して返す関数
 * @returns redux store
 * @param args
 */
export const makeStore = (args?: MakeStoreArgs) => {
  return configureStore({
    reducer,
    preloadedState: args ? args.preloadedState : undefined,
    devTools:
      /* 本番環境ではdev toolが使用できないように設定 */
      isDevelopment,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(rtkQueryMiddleWares),
  });
};
